import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "./MarketingShell";
import Seo from "./Seo";
import FounderSection from "./FounderSection";
import BoardSection from "./BoardSection";
import { ArrowRight, Heart } from "lucide-react";

/**
 * About Us — nonprofit information architecture:
 *
 *   1. Our Mission        (lived-experience heading + body + survivor quote)
 *   2. Our Team           (Founder feature + Board of Directors cards)
 *   3. Five Commitments
 *   4. Call to action     (Try the app + Partner with us)
 *
 * Designed to support donor trust, grant applications, media inquiries, and
 * partnership development by telling a clear nonprofit story: why we exist,
 * who leads it, what we believe, and how visitors can engage.
 */
export default function About() {
  return (
    <MarketingShell>
      <Seo
        title="About Us"
        description="Sister Signal was created from lived experience. Meet the founder, the board of directors, and the commitments that guide our work."
        path="/about"
      />

      {/* SECTION 1 — OUR MISSION ---------------------------------------- */}
      <article
        className="max-w-3xl mx-auto px-6 sm:px-10 pt-20 pb-12"
        data-testid="page-about"
      >
        <p className="font-script text-2xl text-brand">our mission</p>
        <h1 className="mt-2 font-serif text-5xl sm:text-6xl leading-tight">
          Sister Signal was created from lived experience.
        </h1>

        <p className="mt-8 text-xl text-foreground/80 leading-relaxed">
          We provide accessible tools, education, and emotional clarity for individuals navigating
          confusing, unhealthy, or emotionally unsafe relationships.
        </p>

        <p className="mt-6 text-foreground/80 leading-relaxed">
          We believe information, self-trust, and awareness can help people make empowered decisions
          about their future. We believe that clarity, by itself, is a form of protection. And we
          believe that survivors deserve tools built with the same care they were not always shown.
        </p>

        <section
          className="mt-12 rounded-3xl bg-brand-soft/40 border border-brand/20 p-8"
          data-testid="about-survivor-quote"
        >
          <Heart className="w-6 h-6 text-brand" />
          <p className="mt-3 font-serif italic text-2xl text-foreground/90 leading-snug">
            &ldquo;The first time someone gave me language for what I was experiencing, I cried for hours.
            Not because it was sad. Because it meant I wasn&rsquo;t crazy.&rdquo;
          </p>
          <p className="mt-3 text-sm text-foreground/55">— a survivor, in the Sister Signal beta</p>
        </section>
      </article>

      {/* SECTION 2 — OUR TEAM ------------------------------------------- */}
      <section
        className="bg-brand-soft/15 border-y border-brand/10"
        data-testid="section-team"
      >
        <div className="max-w-5xl mx-auto px-6 sm:px-10 pt-16 pb-4">
          <p className="font-script text-2xl text-brand">the people behind sister signal</p>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl leading-tight">Our Team.</h2>
          <p className="mt-3 text-foreground/75 leading-relaxed max-w-2xl">
            Sister Signal is led by a founder with lived experience and supported by a board of
            survivors, builders, educators, and advocates committed to making clarity accessible.
          </p>
        </div>

        {/* Founder feature (full, larger) */}
        <FounderSection />

        {/* Board of Directors grid */}
        <BoardSection />
      </section>

      {/* SECTION 3 — FIVE COMMITMENTS ----------------------------------- */}
      <section
        className="max-w-3xl mx-auto px-6 sm:px-10 py-20"
        data-testid="section-commitments"
      >
        <p className="font-script text-xl text-brand">what we hold to</p>
        <h2 className="font-serif text-3xl mt-1 leading-tight">Five commitments.</h2>
        <ul className="mt-6 space-y-4">
          {[
            ["Trauma-informed first.", "Every screen, copy decision, and interaction is reviewed for whether it could disorient or harm a survivor in a hard moment."],
            ["Discreet by design.", "Quick Exit, code words, and a calculator disguise are baseline features, not premium ones."],
            ["Education over diagnosis.", "We name patterns; we do not diagnose relationships. The survivor remains the only authority on her own story."],
            ["Bilingual access.", "Sister Signal is available in English and Spanish (es-MX) because language should not be a barrier to clarity."],
            ["Free at the point of need.", "Donations and grants keep the core experience free. Survivors should not have to pay to understand what is happening to them."],
          ].map(([h, body]) => (
            <li key={h} className="rounded-2xl bg-card border border-border p-6">
              <p className="font-serif text-lg">{h}</p>
              <p className="mt-1 text-foreground/75 leading-relaxed">{body}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* SECTION 4 — CALL TO ACTION ------------------------------------- */}
      <section
        className="max-w-3xl mx-auto px-6 sm:px-10 pb-24"
        data-testid="section-about-cta"
      >
        <div className="flex flex-wrap gap-3">
          <Link
            to="/app"
            data-testid="about-open-app"
            className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-brand text-white hover:bg-brand/90 transition-all"
          >
            Try the app <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/partners"
            data-testid="about-partner"
            className="inline-flex items-center gap-1 px-6 py-3 rounded-full bg-card border border-border hover:border-brand/50 transition-all"
          >
            Partner with us
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
